import React from 'react';
import {
    Card,
    CardActionArea,
    CardHeader,
    List,
    ListItemButton,
    ListItemText,
    Typography
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { IMenuResponse } from '../../../model/base/response/menu-response';
import { useMenuLinkNavigation } from '../../../hooks/useMenuLinkNavigation';

interface MegaMenuCardProps {
    menu: IMenuResponse;
    onNavigate: () => void;
}

export const MegaMenuCard: React.FC<MegaMenuCardProps> = ({ menu, onNavigate }) => {
    const openMenuLink = useMenuLinkNavigation();
    const allItems = menu.items || [];

    const handleItemClick = (url: string | undefined, isExternal: boolean | undefined) => {
        if (!url) return;

        openMenuLink(url, isExternal);
        onNavigate();
    };

    const header = (
        <CardHeader
            title={
                <Typography variant="subtitle1" fontWeight={700} fontSize="0.95rem">
                    {menu.name}
                </Typography>
            }
            action={menu.url && menu.isExternal
                ? <OpenInNewIcon fontSize="small" sx={{ mt: 1, mr: 1, fontSize: '0.9rem' }} />
                : undefined}
            sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                py: 1.5,
                px: 2,
            }}
        />
    );

    return (
        <Card
            elevation={2}
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.2s ease',
                '&:hover': {
                    boxShadow: 4,
                },
            }}
        >
            {/* Menu de link direto (URL ou página customizada): o próprio card leva ao destino */}
            {menu.url ? (
                <CardActionArea onClick={() => handleItemClick(menu.url, menu.isExternal)}>
                    {header}
                    {menu.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5, fontSize: '0.85rem' }}>
                            {menu.description}
                        </Typography>
                    )}
                </CardActionArea>
            ) : header}
            <List dense disablePadding sx={{ flexGrow: 1 }}>
                {allItems.map((item) => (
                    <ListItemButton
                        key={item.id}
                        onClick={() => handleItemClick(item.url, item.isExternal)}
                        sx={{
                            py: 1,
                            px: 2,
                            '&:hover': {
                                backgroundColor: 'primary.light',
                                '& .MuiListItemText-primary': {
                                    color: 'primary.dark',
                                    fontWeight: 600,
                                },
                            },
                        }}
                    >
                        <ListItemText
                            primary={item.name}
                            primaryTypographyProps={{
                                fontSize: '0.85rem',
                                fontWeight: 500,
                            }}
                        />
                        {item.isExternal && (
                            <OpenInNewIcon
                                fontSize="small"
                                sx={{ ml: 1, fontSize: '0.9rem', color: 'text.secondary' }}
                            />
                        )}
                    </ListItemButton>
                ))}
            </List>
        </Card>
    );
};
